import { useEffect, useState } from 'react';
import { CalendarRange, Check, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  RANGE_LABELS, presetRange, toDateInput, type RangePreset,
} from '@/lib/activityQueries';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * The learning chart's range control.
 *
 * A popover, and a small one. The card sits in the dashboard's side column, so
 * every pixel this takes is a pixel off the chart it is filtering.
 *
 * TWO VIEWS, not one list that grows. Appending the date fields under the four
 * presets made the panel roughly twice as tall the moment anybody looked at
 * Custom — a filter taller than the chart. Custom now REPLACES the list, so
 * the popover is about the same modest height whichever view is showing.
 *
 * The three presets apply on click and close. Custom needs both ends before it
 * means anything, so it applies on its own button — closing on the first date
 * picked would fire a query for a range the user was halfway through choosing.
 */
export function ActivityRangePicker({
  preset,
  from,
  to,
  onChange,
}: {
  preset: RangePreset;
  from: string;
  to: string;
  onChange: (next: { preset: RangePreset; from: string; to: string }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'presets' | 'custom'>('presets');
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);

  /**
   * Reopening starts from what is applied now, not from an abandoned draft.
   * Somebody who opened the custom fields, changed their mind and pressed
   * Escape should not find those dates — or that view — waiting next time.
   */
  useEffect(() => {
    if (open) return;
    setView('presets');
    setDraftFrom(from);
    setDraftTo(to);
  }, [open, from, to]);

  const choosePreset = (next: Exclude<RangePreset, 'CUSTOM'>) => {
    const range = presetRange(next);
    onChange({ preset: next, from: toDateInput(range.from), to: toDateInput(range.to) });
    setOpen(false);
  };

  const applyCustom = () => {
    onChange({ preset: 'CUSTOM', from: draftFrom, to: draftTo });
    setOpen(false);
  };

  const validDraft = draftFrom !== '' && draftTo !== '' && draftFrom <= draftTo;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 shrink-0 gap-1.5 text-xs font-normal">
          <CalendarRange className="h-3.5 w-3.5 text-muted-foreground" />
          {RANGE_LABELS[preset]}
          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-56 p-1">
        {view === 'presets' ? (
          <>
            {(['THIS_MONTH', 'LAST_MONTH', 'SIX_MONTHS'] as const).map((key) => (
              <Row key={key} selected={preset === key} onClick={() => choosePreset(key)}>
                {RANGE_LABELS[key]}
                {preset === key && <Check className="h-3.5 w-3.5 text-brand-700" />}
              </Row>
            ))}

            <Row selected={preset === 'CUSTOM'} onClick={() => setView('custom')}>
              {RANGE_LABELS.CUSTOM}
              {/* A chevron, because this row opens a view rather than applying. */}
              <ChevronRight className="h-3.5 w-3.5 opacity-40" />
            </Row>
          </>
        ) : (
          <div className="space-y-1.5 p-1">
            <button
              type="button"
              onClick={() => setView('presets')}
              className="-ml-1 flex items-center gap-0.5 rounded px-1 py-0.5 text-[11px] text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:text-foreground"
            >
              <ChevronLeft className="h-3 w-3" />
              Back
            </button>

            {/*
              Labels inline rather than stacked above the field: two label
              lines were 40px of a panel this size, for two words each.
            */}
            <label className="flex items-center gap-2">
              <span className="w-8 shrink-0 text-[11px] text-muted-foreground">From</span>
              <Input
                type="date"
                value={draftFrom}
                max={draftTo || undefined}
                onChange={(e) => setDraftFrom(e.target.value)}
                className="h-7 flex-1 px-2 text-xs"
              />
            </label>

            <label className="flex items-center gap-2">
              <span className="w-8 shrink-0 text-[11px] text-muted-foreground">To</span>
              <Input
                type="date"
                value={draftTo}
                min={draftFrom || undefined}
                onChange={(e) => setDraftTo(e.target.value)}
                className="h-7 flex-1 px-2 text-xs"
              />
            </label>

            {/*
              Says why Apply is dead rather than leaving it greyed and silent.
              Only once both ends exist — an incomplete range is not yet a
              mistake, and the message would flash on every first click.
            */}
            {draftFrom !== '' && draftTo !== '' && !validDraft && (
              <p className="text-[11px] leading-tight text-destructive">
                Start must come before end.
              </p>
            )}

            <Button size="sm" className="h-7 w-full text-xs" disabled={!validDraft} onClick={applyCustom}>
              Apply
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function Row({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-[13px] transition-colors',
        'hover:bg-muted focus:bg-muted focus:outline-none',
        selected && 'font-medium',
      )}
    >
      {children}
    </button>
  );
}
